import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runDatasetQuery } from "@/lib/reporting/engine.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { KpiCard } from "@/components/reporting/kpi-card";
import { ChartCard } from "@/components/reporting/chart-card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { ArrowDownToLine, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { useMemo } from "react";
import { useReportsRange } from "@/hooks/use-reports-range";

export const Route = createFileRoute("/_authenticated/app/reports/receiving")({ component: ReceivingDash });

function ReceivingDash() {
  const { tenantId, facilityId } = useScope();
  const run = useServerFn(runDatasetQuery);
  const { sinceIso: since, untilIso: until, days: windowDays } = useReportsRange(30);
  const rangeFilter = (column: string) => {
    const conds: any[] = [{ type: "condition" as const, column, op: "gte" as const, value: since }];
    if (until) conds.push({ type: "condition" as const, column, op: "lte" as const, value: until });
    if (facilityId) conds.push({ type: "condition" as const, column: "facility_id", op: "eq" as const, value: facilityId });
    return conds.length === 1 ? conds[0] : { type: "and" as const, conditions: conds };
  };

  const { data: byStatus, isLoading } = useQuery({
    queryKey: ["rpt-rcv-status", tenantId, facilityId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "receipts",
      filters: rangeFilter("created_at"),
      group: ["status"],
      aggregations: [{ column: "id", fn: "count", alias: "count" }],
      limit: 50, offset: 0,
    }}),
  });

  const { data: byCarrier } = useQuery({
    queryKey: ["rpt-rcv-carrier", tenantId, facilityId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "receipts",
      filters: rangeFilter("created_at"),
      group: ["carrier"],
      aggregations: [{ column: "id", fn: "count", alias: "count" }],
      limit: 20, offset: 0,
    }}),
  });

  const totals = useMemo(() => {
    const rows = byStatus?.rows ?? [];
    const counts: Record<string, number> = {};
    let total = 0;
    for (const r of rows) { const n = Number(r.count ?? 0); counts[r.status ?? "—"] = n; total += n; }
    return {
      total,
      received: counts["received"] ?? 0,
      open: (counts["draft"] ?? 0) + (counts["expected"] ?? 0) + (counts["in_progress"] ?? 0),
      cancelled: counts["cancelled"] ?? 0,
    };
  }, [byStatus]);

  const statusData = (byStatus?.rows ?? []).map((r: any) => ({ status: r.status ?? "—", count: Number(r.count ?? 0) }));
  const carrierData = (byCarrier?.rows ?? [])
    .map((r: any) => ({ carrier: r.carrier ?? "—", count: Number(r.count ?? 0) }))
    .sort((a, b) => b.count - a.count).slice(0, 10);

  return (
    <>
      <PageHeader title="Receiving" description={`ASN inflow and status mix over the selected window (${windowDays} day${windowDays === 1 ? "" : "s"}).`} />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="ASNs total" value={totals.total} icon={<ArrowDownToLine className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Received" value={totals.received} icon={<CheckCircle2 className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Open" value={totals.open} icon={<Clock className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Cancelled" value={totals.cancelled} icon={<AlertCircle className="h-4 w-4" />} loading={isLoading} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="ASNs by Status">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="status" fontSize={11} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="var(--primary)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="ASNs by Carrier">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={carrierData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="carrier" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="var(--primary)" name="ASNs" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}